import { addItemToCart } from './cart-service.js';
import { propertiesFromPlacement, applyToFormData, toParams } from './rebuy-attribution.js';

/**
 * Product Card Custom Element
 * Handles add to cart form submission for product cards
 */

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.submitButton = null;
    this.variantInput = null;
    this.variantSelect = null;
    this.isSubmitting = false;

    // Bind methods
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    // Find form and elements
    this.form = this.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('button[type="submit"]');
    // The variant id comes from a hidden input, or from the card's variant
    // dropdown when the threshold setting puts one on this product.
    this.variantInput = this.form.querySelector('[name="id"]');
    this.variantSelect = this.form.querySelector('select[name="id"]');

    // Add submit handler to prevent default form submission
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit);
    }
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.handleVariantChange);
    }
  }

  disconnectedCallback() {
    // Remove event listeners
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
    }
    if (this.variantSelect) {
      this.variantSelect.removeEventListener('change', this.handleVariantChange);
    }
  }

  /**
   * Show the price row for the selected variant (rows are server-rendered).
   */
  handleVariantChange() {
    const variantId = this.variantSelect.value;
    this.querySelectorAll('[data-price-for]').forEach((row) => {
      row.hidden = row.getAttribute('data-price-for') !== variantId;
    });
  }

  /**
   * Handle form submission
   * Prevents default form submission and uses AJAX to add to cart
   */
  async handleSubmit(event) {
    event.preventDefault();

    // Prevent multiple simultaneous submissions
    if (this.isSubmitting) return;

    if (!this.form || !this.variantInput) return;

    const variantId = this.variantInput.value;
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    // Get quantity from form (default to 1 if not present)
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    this.isSubmitting = true;

    // Disable button during submission
    const btnDisabledText = this.submitButton.querySelector('[disabled-text]');
    const originalButtonText = btnDisabledText ? btnDisabledText.textContent : null;
    this.submitButton.disabled = true;
    const loadingText = window.umbrellaConfig.addToCart.submit;
    if (btnDisabledText) {
      btnDisabledText.textContent = loadingText;
    }

    const formData = new FormData(this.form);

    // Cards rendered inside a Rebuy recommendations placement carry the
    // standard Rebuy line item properties so the order attributes in Rebuy.
    applyToFormData(formData, propertiesFromPlacement(this.closest('rebuy-recommendations')));

    try {
      await addItemToCart(variantId, quantity, formData);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error.message || 'Failed to add item to cart. Please try again.');
    } finally {
      this.isSubmitting = false;

      // Restore button text and state
      if (this.submitButton) {
        if (btnDisabledText && originalButtonText) {
          btnDisabledText.textContent = originalButtonText;
        }
        this.submitButton.disabled = false;
      }
    }
  }
}

// Register the custom element
customElements.define('product-card', ProductCard);

// Click-throughs from a Rebuy placement carry the placement's attribution as
// _rb_* URL params ("View Options" buttons, "See more options" links, and the
// compact cards' overlay links). The product page stashes them on load and
// attaches the properties to the add — see rebuy-attribution.js.
document.addEventListener('click', (event) => {
  const link = event.target && event.target.closest && event.target.closest('a[href]');
  if (!link) return;
  const placement = link.closest('rebuy-recommendations');
  if (!placement) return;
  const properties = propertiesFromPlacement(placement);
  if (!properties) return;
  try {
    const url = new URL(link.getAttribute('href'), window.location.origin);
    toParams(properties).forEach((value, key) => url.searchParams.set(key, value));
    link.setAttribute('href', url.toString());
  } catch (e) {}
}, true);
